import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader, Spinner, Surface, CopyButton } from "@/components/common";
import { RefinePanel } from "@/components/RefinePanel";
import { AttachmentUploader } from "@/components/AttachmentUploader";
import { LanguageSelect } from "@/components/LanguageSelect";
import { useLocale } from "@/context/LocaleContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

const VERSIONS = [
  { key: "professional", label: "Professionel" },
  { key: "personal", label: "Personlig" },
  { key: "sales", label: "Salgsorienteret" },
];

export default function LinkedInPage() {
  const location = useLocation();
  const { t, contentLang } = useLocale();
  const [topic, setTopic] = useState("");
  const [result, setResult] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [language, setLanguage] = useState(contentLang);

  const navigate = useNavigate();
  useEffect(() => {
    const initial = location.state?.topic || new URLSearchParams(location.search).get("emne");
    if (initial) {
      setTopic(initial);
      generate(initial);
      if (location.search) navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const generate = async (arg) => {
    const value = typeof arg === "string" ? arg : topic;
    if (!value.trim()) return toast.error(t("Skriv hvad opslaget skal handle om"));
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/linkedin", { topic: value, language, attachment_ids: attachments.map((a) => a.id) });
      setResult(data.result);
      setProjectId(data.project_id);
      toast.success(t("Tre versioner klar — gemt automatisk"));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader overline="LinkedIn-opslag" title="Skriv et opslag, der bliver læst"
        subtitle="Skriv kort hvad det skal handle om — så får du tre versioner i jeres tone." />

      <div className="space-y-3 mb-8">
        <Textarea data-testid="linkedin-topic" value={topic} onChange={(e) => setTopic(e.target.value)}
          rows={3} placeholder={t("Hvad skal opslaget handle om?")} />
        <AttachmentUploader files={attachments} onChange={setAttachments} disabled={loading} />
        <div className="flex flex-wrap items-center gap-3">
          <LanguageSelect value={language} onChange={setLanguage} disabled={loading} testid="linkedin-language" />
          <Button onClick={() => generate()} disabled={loading} className="gap-2" data-testid="linkedin-generate-btn">
            <Sparkles className="h-4 w-4" /> {t("Skriv tre versioner")}
          </Button>
        </div>
      </div>

      {loading && <Spinner label="Skriver jeres opslag..." />}
      {result && (
        <Tabs defaultValue="professional" data-testid="linkedin-result">
          <TabsList className="mb-4">
            {VERSIONS.map((v) => <TabsTrigger key={v.key} value={v.key} data-testid={`linkedin-tab-${v.key}`}>{t(v.label)}</TabsTrigger>)}
          </TabsList>
          {VERSIONS.map((v) => (
            <TabsContent key={v.key} value={v.key}>
              <Surface testid={`linkedin-version-${v.key}`}>
                <div className="flex justify-end mb-3"><CopyButton text={result[v.key]} testid={`linkedin-copy-${v.key}`} /></div>
                <Textarea value={result[v.key]} onChange={(e) => setResult({ ...result, [v.key]: e.target.value })}
                  rows={12} className="font-sans leading-relaxed resize-none border-0 bg-transparent px-0 focus-visible:ring-0" data-testid={`linkedin-text-${v.key}`} />
              </Surface>
            </TabsContent>
          ))}
        </Tabs>
      )}
      {result && (
        <div className="mt-4">
          <RefinePanel result={result} setResult={setResult} module="linkedin" projectId={projectId} language={language}
            quick={["Gør det kortere", "Mere personligt", "Tydeligere opfordring", "Mere fagligt", "Tilføj emojis"].map((q) => t(q))} />
        </div>
      )}
    </div>
  );
}
